import type {
  HsCandidateGroup,
  NeedsInputField,
  NexaEvent,
  NexaResumeResponse,
  NexaStoredMessage,
  NexaThreadSummary,
  RefusalReason,
  Source,
} from "./types";

interface WireSource {
  tool: string;
  authoritative: boolean;
  as_of: string;
}

interface WireHsCandidateGroup {
  product: string;
  candidates: { code: string; description: string; confirmed: boolean }[];
  if_none_match?: string | null;
}

interface WireNeedsInputField {
  field: string;
  why_needed: string;
  how_to_obtain: string | null;
  options?: string[] | null;
}

export interface WireCreatedThread {
  thread_id?: string;
}

export interface WireThreadSummary {
  thread_id: string;
  title: string;
  updated_at: string;
}

export interface WireStoredMessage {
  role: string;
  content: string;
  created_at: string;
}

export interface WireResumeResponse {
  thread_id: string;
  context: unknown;
  messages: WireStoredMessage[];
}

export type WireEvent =
  | { event: "ready"; thread_id: string }
  | { event: "turn.started"; thread_id: string; asked_at: string }
  | { event: "tool.started"; tool: string; label: string }
  | { event: "composing"; label: string }
  | { event: "turn.answer"; text: string; format: "markdown"; sources?: WireSource[]; hs_candidates?: WireHsCandidateGroup[] }
  | { event: "turn.needs_input"; prompt: string; fields?: WireNeedsInputField[] }
  | { event: "turn.refused"; reason: RefusalReason; text: string }
  | { event: "error"; message: string };

const toSource = (row: WireSource): Source => ({
  tool: row.tool,
  authoritative: row.authoritative,
  asOf: row.as_of,
});

const toHsCandidateGroup = (row: WireHsCandidateGroup): HsCandidateGroup => ({
  product: row.product,
  candidates: row.candidates ?? [],
  ifNoneMatch: row.if_none_match,
});

const toNeedsInputField = (row: WireNeedsInputField): NeedsInputField => ({
  field: row.field,
  whyNeeded: row.why_needed,
  howToObtain: row.how_to_obtain,
  options: row.options,
});

export const toThreadSummary = (row: WireThreadSummary): NexaThreadSummary => ({
  threadId: row.thread_id,
  title: row.title,
  updatedAt: row.updated_at,
});

export const toStoredMessage = (row: WireStoredMessage): NexaStoredMessage => ({
  role: row.role,
  content: row.content,
  createdAt: row.created_at,
});

export const toResumeResponse = (row: WireResumeResponse): NexaResumeResponse => ({
  threadId: row?.thread_id,
  context: row?.context,
  messages: (row?.messages ?? []).map(toStoredMessage),
});

export const toNexaEvent = (raw: WireEvent): NexaEvent | null => {
  switch (raw?.event) {
    case "ready":
      return { event: "ready", threadId: raw.thread_id };
    case "turn.started":
      return { event: "turn.started", threadId: raw.thread_id, askedAt: raw.asked_at };
    case "tool.started":
      return { event: "tool.started", tool: raw.tool, label: raw.label };
    case "composing":
      return { event: "composing", label: raw.label };
    case "turn.answer":
      return {
        event: "turn.answer",
        text: raw.text,
        format: raw.format,
        sources: (raw.sources ?? []).map(toSource),
        hsCandidates: raw.hs_candidates?.map(toHsCandidateGroup),
      };
    case "turn.needs_input":
      return { event: "turn.needs_input", prompt: raw.prompt, fields: (raw.fields ?? []).map(toNeedsInputField) };
    case "turn.refused":
      return { event: "turn.refused", reason: raw.reason, text: raw.text };
    case "error":
      return { event: "error", message: raw.message };
    default:
      return null;
  }
};
